// src/pages/VendorSettings.jsx
import React, { useState, useEffect } from 'react';
import { Header } from '../components';
import { FiSettings, FiCheck, FiAlertTriangle, FiX } from 'react-icons/fi';
import axios from 'axios';
import { useStateContext } from '../contexts/ContextProvider';

// Simple inline notification component
const InlineNotification = ({ message, type, onClose }) => {
  const bgColor = type === 'success' ? 'bg-green-100 border-green-400 text-green-700' 
                : type === 'error' ? 'bg-red-100 border-red-400 text-red-700'
                : 'bg-blue-100 border-blue-400 text-blue-700';
  
  const Icon = type === 'success' ? FiCheck 
             : type === 'error' ? FiAlertTriangle
             : FiCheck;

  return (
    <div className={`${bgColor} px-4 py-3 rounded border mb-4 relative flex items-center`} role="alert">
      <Icon className="mr-2" />
      <span className="block sm:inline">{message}</span>
      <button 
        className="absolute top-0 bottom-0 right-0 px-4 py-3 flex items-center"
        onClick={onClose}
      >
        <FiX />
      </button>
    </div>
  );
};

const VendorSettings = () => {
  const { currentColor } = useStateContext();
  
  // Commission state
  const [commissionPercentage, setCommissionPercentage] = useState(15);
  const [newCommissionPercentage, setNewCommissionPercentage] = useState('');
  const [isCommissionEditing, setIsCommissionEditing] = useState(false);
  
  // Convenience fees state
  const [convenienceFeesPercentage, setConvenienceFeesPercentage] = useState(2.5);
  const [newConvenienceFeesPercentage, setNewConvenienceFeesPercentage] = useState('');
  const [isConvenienceFeesEditing, setIsConvenienceFeesEditing] = useState(false);
  
  // Common state
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  
  // Get the token from localStorage
  const token = localStorage.getItem('token');
  
  // Setup headers according to your middleware
  const headers = {
    'Authorization': `Bearer ${token}`
  };
  
  // Show notification
  const showNotification = (message, type = 'info') => {
    setNotification({ message, type });
    
    // Auto-dismiss after 3 seconds
    const timer = setTimeout(() => {
      setNotification(null);
    }, 3000);
    
    return () => clearTimeout(timer);
  };
  
  // Fetch current commission percentage
  const fetchCommissionPercentage = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/commission-percentage`,
        { headers }
      );
      
      if (response.data.success) {
        setCommissionPercentage(response.data.data.commissionPercentage);
        setNewCommissionPercentage(response.data.data.commissionPercentage.toString());
      } else {
        showNotification('Failed to fetch commission percentage', 'error');
      }
    } catch (error) {
      console.error('Error fetching commission percentage:', error);
      showNotification(error.response?.data?.message || 'Failed to fetch commission percentage', 'error');
    }
  };
  
  // Fetch current convenience fees percentage
  const fetchConvenienceFeesPercentage = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/convenience-fees-percentage`,
        { headers }
      );
      
      if (response.data.success) {
        setConvenienceFeesPercentage(response.data.data.convenienceFeesPercentage);
        setNewConvenienceFeesPercentage(response.data.data.convenienceFeesPercentage.toString());
      } else {
        showNotification('Failed to fetch convenience fees percentage', 'error');
      }
    } catch (error) {
      console.error('Error fetching convenience fees percentage:', error);
      showNotification(error.response?.data?.message || 'Failed to fetch convenience fees percentage', 'error');
    }
  };
  
  // Update commission percentage
  const updateCommissionPercentage = async () => {
    try {
      setIsLoading(true);
      
      // Validate input
      const percentage = parseFloat(newCommissionPercentage);
      if (isNaN(percentage) || percentage < 0 || percentage > 100) {
        showNotification('Commission percentage must be between 0 and 100', 'error');
        return;
      }
      
      const response = await axios.patch(
        `${process.env.REACT_APP_API_URL}/api/admin/commission-percentage`,
        { commissionPercentage: percentage },
        { headers }
      );
      
      if (response.data.success) {
        setCommissionPercentage(percentage);
        showNotification('Commission percentage updated successfully', 'success');
        setIsCommissionEditing(false);
      } else {
        showNotification(response.data.message || 'Failed to update commission percentage', 'error');
      }
    } catch (error) {
      console.error('Error updating commission percentage:', error);
      showNotification(error.response?.data?.message || 'Failed to update commission percentage', 'error');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Update convenience fees percentage
  const updateConvenienceFeesPercentage = async () => {
    try {
      setIsLoading(true);
      
      // Validate input
      const percentage = parseFloat(newConvenienceFeesPercentage);
      if (isNaN(percentage) || percentage < 0 || percentage > 100) {
        showNotification('Convenience fees percentage must be between 0 and 100', 'error');
        return;
      }
      
      const response = await axios.patch(
        `${process.env.REACT_APP_API_URL}/api/admin/convenience-fees-percentage`,
        { convenienceFeesPercentage: percentage },
        { headers }
      );
      
      if (response.data.success) {
        setConvenienceFeesPercentage(percentage);
        showNotification('Convenience fees percentage updated successfully', 'success');
        setIsConvenienceFeesEditing(false);
      } else {
        showNotification(response.data.message || 'Failed to update convenience fees percentage', 'error');
      }
    } catch (error) {
      console.error('Error updating convenience fees percentage:', error);
      showNotification(error.response?.data?.message || 'Failed to update convenience fees percentage', 'error');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle commission form submission
  const handleCommissionSubmit = (e) => {
    e.preventDefault();
    updateCommissionPercentage();
  };
  
  // Handle convenience fees form submission
  const handleConvenienceFeesSubmit = (e) => {
    e.preventDefault();
    updateConvenienceFeesPercentage();
  };
  
  // Fetch both percentages on component mount
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      await Promise.all([
        fetchCommissionPercentage(),
        fetchConvenienceFeesPercentage()
      ]);
      setIsLoading(false);
    };
    
    fetchData();
  }, []);
  
  return (
    <div className="card p-5 md:p-8">
      {notification && (
        <InlineNotification 
          message={notification.message} 
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}
      
      <Header category="Settings" title="Vendor Settings" />
      
      <div className="flex items-center mb-8">
        <FiSettings className="text-2xl mr-2" />
        <h2 className="text-xl font-semibold">Fee Settings</h2>
      </div>
      
      {/* Commission Settings */}
      <div className="bg-gray-50 p-6 rounded-lg shadow-sm mb-8">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-lg font-medium">Commission Percentage</h3>
            <p className="text-gray-600 text-sm mt-1">
              The percentage deducted from vendor earnings as platform commission
            </p>
          </div>
          
          {!isCommissionEditing && (
            <button
              type="button"
              className="px-4 py-2 text-sm font-medium text-white rounded-md"
              style={{ backgroundColor: currentColor }}
              onClick={() => setIsCommissionEditing(true)}
              disabled={isLoading}
            >
              Edit
            </button>
          )}
        </div>
        
        {isCommissionEditing ? (
          <form onSubmit={handleCommissionSubmit} className="mt-4">
            <div className="mb-4">
              <label htmlFor="commissionPercentage" className="block text-sm font-medium text-gray-700 mb-1">
                Commission Percentage (%)
              </label>
              <div className="mt-1 flex rounded-md shadow-sm">
                <input
                  type="number"
                  name="commissionPercentage"
                  id="commissionPercentage"
                  className="flex-1 min-w-0 block w-full px-3 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="Enter percentage (0-100)"
                  value={newCommissionPercentage}
                  onChange={(e) => setNewCommissionPercentage(e.target.value)}
                  min="0"
                  max="100"
                  step="0.1"
                  required
                />
                <span className="inline-flex items-center px-3 rounded-r-md border border-l-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm">
                  %
                </span>
              </div>
              <p className="mt-2 text-sm text-gray-500">
                This percentage will be applied to all vendor earnings
              </p>
            </div>
            
            <div className="flex space-x-3">
              <button
                type="submit"
                className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white focus:outline-none"
                style={{ backgroundColor: currentColor }}
                disabled={isLoading}
              >
                {isLoading ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                type="button"
                className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
                onClick={() => {
                  setIsCommissionEditing(false);
                  setNewCommissionPercentage(commissionPercentage.toString());
                }}
                disabled={isLoading}
              >
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <div className="mt-4 bg-white p-4 rounded-md border border-gray-200">
            <div className="flex items-baseline">
              <span className="text-3xl font-bold">{commissionPercentage}</span>
              <span className="ml-1 text-xl">%</span>
            </div>
            <p className="mt-1 text-sm text-gray-500">
              Last updated: {new Date().toLocaleDateString()}
            </p>
          </div>
        )}
      </div>
      
      {/* Convenience Fees Settings */}
      <div className="bg-gray-50 p-6 rounded-lg shadow-sm mb-8">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-lg font-medium">Convenience Fees Percentage</h3>
            <p className="text-gray-600 text-sm mt-1">
              The percentage charged to customers as convenience fees for online payments
            </p>
          </div>
          
          {!isConvenienceFeesEditing && (
            <button
              type="button"
              className="px-4 py-2 text-sm font-medium text-white rounded-md"
              style={{ backgroundColor: currentColor }}
              onClick={() => setIsConvenienceFeesEditing(true)}
              disabled={isLoading}
            >
              Edit
            </button>
          )}
        </div>
        
        {isConvenienceFeesEditing ? (
          <form onSubmit={handleConvenienceFeesSubmit} className="mt-4">
            <div className="mb-4">
              <label htmlFor="convenienceFeesPercentage" className="block text-sm font-medium text-gray-700 mb-1">
                Convenience Fees Percentage (%)
              </label>
              <div className="mt-1 flex rounded-md shadow-sm">
                <input
                  type="number"
                  name="convenienceFeesPercentage"
                  id="convenienceFeesPercentage"
                  className="flex-1 min-w-0 block w-full px-3 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="Enter percentage (0-100)"
                  value={newConvenienceFeesPercentage}
                  onChange={(e) => setNewConvenienceFeesPercentage(e.target.value)}
                  min="0"
                  max="100"
                  step="0.1"
                  required
                />
                <span className="inline-flex items-center px-3 rounded-r-md border border-l-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm">
                  %
                </span>
              </div>
              <p className="mt-2 text-sm text-gray-500">
                This percentage will be added to customer bills for online payments
              </p>
            </div>
            
            <div className="flex space-x-3">
              <button
                type="submit"
                className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white focus:outline-none"
                style={{ backgroundColor: currentColor }}
                disabled={isLoading}
              >
                {isLoading ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                type="button"
                className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
                onClick={() => {
                  setIsConvenienceFeesEditing(false);
                  setNewConvenienceFeesPercentage(convenienceFeesPercentage.toString());
                }}
                disabled={isLoading}
              >
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <div className="mt-4 bg-white p-4 rounded-md border border-gray-200">
            <div className="flex items-baseline">
              <span className="text-3xl font-bold">{convenienceFeesPercentage}</span>
              <span className="ml-1 text-xl">%</span>
            </div>
            <p className="mt-1 text-sm text-gray-500">
              Last updated: {new Date().toLocaleDateString()}
            </p>
          </div>
        )}
      </div>
      
      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
        <h4 className="text-blue-800 font-medium mb-2">Important Information</h4>
        <ul className="text-blue-700 text-sm space-y-1">
          <li>• Commission percentage applies to all vendor transactions and affects their earnings</li>
          <li>• Convenience fees are charged to customers for online payment processing</li>
          <li>• Both settings are applied globally across the platform</li>
          <li>• Changes will take effect immediately for new transactions</li>
        </ul>
      </div>
    </div>
  );
};

export default VendorSettings;